import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react'
import { Button, Typography, Skeleton, Tag, App as AntdApp } from 'antd'
import { PlusOutlined, LoadingOutlined, SyncOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'

import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { notificationStore } from '../../context/notificationsContext/store'
import { useUserDashboardData, TeamLeaveData } from './hooks/useUserDashboardData'
import { IUserLeaveTypeResult } from '../../types/custom-queries'
import { IDashboardLeaveRequest } from '@vacationtracker/shared/types/leave-request'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import { calculateQuickStats, generateAlertBanners, getPrimaryLeaveType, calculateTeamAvailability, getTimeBasedGreeting } from './utils/dashboardCalculations'

import { BalanceCard, UpcomingLeavesCard, AlertBanner, TeamAvailability, QuickStats, UpcomingHolidays } from './components'
import { isToilLeave } from '@vacationtracker/shared/functions/is-toil-leave-request'
import Api from '@vacationtracker/shared/services/api'

import './styles/UserDashboard.less'
import IntlMessages from '../../util/IntlMessages'
import { dashboardTracker } from './services/userDashboardTracking'
import { IQuotas } from '@vacationtracker/shared/types/leave-policy'
import { roundTo2Decimals } from '@vacationtracker/shared/functions/calculation-shared'
import isEqual from 'lodash/isEqual'

export interface IRolloverExpiry {
  unit: string
  totalRollover: number
  leaveTypeName: string
  expired?: number
  willExpire?: number
  expiryDate?: string
}

// Import debug utilities in development
if (process.env.NODE_ENV === 'development') {
  import('./utils/dashboardDebug')
}

const { Title, Text } = Typography

const DashboardPageForUsers: React.FC = () => {
  const { notification } = AntdApp.useApp()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const [localActionNotifications, setLocalActionNotifications] = useState<string[]>([])

  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<string>('')
  const [selectedLeaveType, setSelectedLeaveType] = useState<IUserLeaveTypeResult | null>(null)
  const [loadStartTime] = useState<number>(Date.now())
  const [hasTrackedPageVisit, setHasTrackedPageVisit] = useState<boolean>(false)
  const [loadTimeoutId, setLoadTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const [companyLeaves, setCompanyLeaves] = useState<TeamLeaveData[]>([])
  const [companyUsers, setCompanyUsers] = useState<Array<{ id: string; name: string; status: string; locationId?: string }>>([])
  const [companyLocations, setCompanyLocations] = useState<
    Array<{
      id: string
      name: string
      holidays?: Array<{
        year: number
        holidays: Array<{
          name: string
          date: string
          isHalfDay?: boolean
        }>
      }>
    }>
  >([])
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState<boolean>(false)

  const {
    user,
    company,
    teamLeaves,
    companyLeaves: dataCompanyLeaves,
    teamUsers,
    locations,
    visibilityAddon,
    isLoading,
    error,
    refetch,
    fetchCompanyWideData,
    isFromCache,
    cacheAge,
  } = useUserDashboardData({
    userId: authUser.id,
    autoFetch: true,
  })

  // Store refetch in a ref to avoid dependency issues
  const refetchRef = useRef(refetch)
  useEffect(() => {
    refetchRef.current = refetch
  }, [refetch])

  // Set up load timeout tracking
  useEffect(() => {
    if (isLoading && !user) {
      // Set a 10-second timeout for dashboard loading
      const timeoutId = setTimeout(() => {
        dashboardTracker.trackError({
          errorType: 'dashboard_load_error',
          errorMessage: 'Dashboard failed to load within timeout period',
          sectionAffected: 'dashboard',
          hasCachedData: false,
          retryAttempted: false,
          autoRecoveryPossible: true,
        })
      }, 10000) // 10 seconds

      setLoadTimeoutId(timeoutId)
    } else if (!isLoading || user) {
      // Clear timeout if data loads successfully
      if (loadTimeoutId) {
        clearTimeout(loadTimeoutId)
        setLoadTimeoutId(null)
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (loadTimeoutId) {
        clearTimeout(loadTimeoutId)
      }
    }
  }, [isLoading, user, loadTimeoutId])

  useEffect(() => {
    if (!user) return
    const currentYearLeaveTypes = user.leaveDays.find((ld) => dayjs().isBetween(ld.yearStart, ld.yearEnd, 'day', '[]'))

    if (currentYearLeaveTypes) {
      const leaveTypes = (currentYearLeaveTypes?.leaveTypes || []) as IUserLeaveTypeResult[]
      setSelectedLeaveType(leaveTypes.find((lt) => lt.leaveTypeId === selectedLeaveTypeId) || null)
    }
  }, [user?.leaveDays, selectedLeaveTypeId])

  // Initialize tracking and set primary leave type when data loads
  useEffect(() => {
    // Initialize tracker on component mount
    dashboardTracker.init()

    const currentYearLeaveTypes = user?.leaveDays.find((ld) => dayjs().isBetween(ld.yearStart, ld.yearEnd, 'day', '[]'))

    if (user && currentYearLeaveTypes && !selectedLeaveTypeId) {
      const leaveTypes = currentYearLeaveTypes.leaveTypes as IUserLeaveTypeResult[]

      const primaryLeaveType = getPrimaryLeaveType(leaveTypes)
      if (primaryLeaveType) {
        setSelectedLeaveTypeId(primaryLeaveType.leaveTypeId)
      }
    }

    // Cleanup tracking on component unmount
    return () => {
      dashboardTracker.cleanup()
    }
  }, [user, selectedLeaveTypeId])

  // Track page visit once data is loaded
  useEffect(() => {
    if (!hasTrackedPageVisit && user && !isLoading) {
      const currentYearLeaveTypes = user.leaveDays.find((ld) => dayjs().isBetween(ld.yearStart, ld.yearEnd, 'day', '[]'))
      const leaveTypes = (currentYearLeaveTypes?.leaveTypes as IUserLeaveTypeResult[]) || []
      const upcomingLeaves = [...(user.upcomingLeaves || []), ...(user.pendingLeaves || [])]

      dashboardTracker.trackVisited(user, leaveTypes, upcomingLeaves as unknown as IDashboardLeaveRequest[])
      setHasTrackedPageVisit(true)

      // Track load time
      const loadTime = Date.now() - loadStartTime
      dashboardTracker.trackLoadTime({
        totalLoadTime: loadTime,
        graphqlResponseTime: loadTime, // This could be more precise if we track GraphQL separately
        cachedDataUsed: isFromCache,
        dataSize: JSON.stringify({ user, teamLeaves, teamUsers }).length / 1024, // Rough estimate in KB
      })

      // Track initial balance check
      const primaryLeaveType = getPrimaryLeaveType(leaveTypes)
      if (primaryLeaveType) {
        dashboardTracker.trackBalanceChecked({
          leaveTypeViewed: primaryLeaveType.leaveTypeName,
          balanceAmount: primaryLeaveType.currentDays,
          hasMinutesPrecision: primaryLeaveType.shortestLeaveInterval ? ['ONE-HOUR', 'HALF-HOUR', 'FIFTEEN-MINUTES'].includes(primaryLeaveType.shortestLeaveInterval) : false,
          isNegative: primaryLeaveType.currentDays < 0,
          isUnlimited: primaryLeaveType.hasUnlimitedDays,
        })
      }
    }
  }, [user, isLoading, hasTrackedPageVisit, loadStartTime])

  // Listen to actionNotifications and refetch dashboard data when our operations complete
  // This prevents infinite loops by only refetching when operations we initiated complete
  useEffect(() => {
    if (!isEqual(actionNotifications, localActionNotifications)) {
      setLocalActionNotifications(actionNotifications)
      if (process.env.NODE_ENV === 'development') {
        console.log('[Dashboard] Completed operations detected:', actionNotifications)
      }

      // Refetch dashboard data since our operations completed
      void refetchRef.current()
    }
  }, [actionNotifications, localActionNotifications])

  // Track errors with enhanced categorization
  useEffect(() => {
    if (error) {
      // Determine error type based on error message/content
      let errorType: 'network_error' | 'graphql_error' | 'permission_error' | 'parse_error' | 'dashboard_load_error' = 'graphql_error'

      if (error.message?.includes('Network') || error.message?.includes('fetch')) {
        errorType = 'network_error'
      } else if (error.message?.includes('permission') || error.message?.includes('unauthorized')) {
        errorType = 'permission_error'
      } else if (error.message?.includes('parse') || error.message?.includes('JSON')) {
        errorType = 'parse_error'
      }

      dashboardTracker.trackError({
        errorType,
        errorMessage: error.message || 'Unknown error',
        sectionAffected: 'dashboard',
        hasCachedData: !!user,
        retryAttempted: false,
        autoRecoveryPossible: true,
      })
    }
  }, [error, user])

  // Handle navigation actions with tracking
  const handleRequestLeave = useCallback(() => {
    dashboardTracker.trackActionTaken({
      actionType: 'button_click',
      actionName: 'request_leave',
      elementType: 'button',
      section: 'header',
      currentLeaveType: selectedLeaveType?.leaveTypeName || 'unknown',
      currentBalance: selectedLeaveType?.currentDays || 0,
      ledToNavigation: true,
      destinationUrl: '/app/request-leave',
    })

    history.push('/app/request-leave')
  }, [user?.leaveDays, selectedLeaveType, history])

  const handleViewAllLeaves = useCallback(() => {
    dashboardTracker.trackActionTaken({
      actionType: 'button_click',
      actionName: 'view_all_leaves',
      elementType: 'button',
      section: 'header',
      currentLeaveType: selectedLeaveType?.leaveTypeName || 'unknown',
      currentBalance: selectedLeaveType?.currentDays || 0,
      ledToNavigation: true,
      destinationUrl: '/app/calendar',
    })

    history.push('/app/calendar')
  }, [user?.leaveDays, selectedLeaveType, history])

  const handleViewProfile = useCallback(() => {
    dashboardTracker.trackActionTaken({
      actionType: 'button_click',
      actionName: 'view_profile',
      elementType: 'button',
      section: 'upcoming_leaves',
      currentLeaveType: selectedLeaveType?.leaveTypeName || 'unknown',
      currentBalance: selectedLeaveType?.currentDays || 0,
      ledToNavigation: true,
      destinationUrl: '/app/my-profile',
    })

    history.push('/app/my-profile')
  }, [user?.leaveDays, selectedLeaveType, history])

  // Handle view scope change for team availability
  const handleViewScopeChange = useCallback(
    (scope: 'department' | 'everyone') => {
      if (scope === 'everyone') {
        // Use already fetched company-wide leaves and fetch all users
        setIsLoadingCompanyData(true)
        // Run async operation without returning the promise
        ;(async () => {
          try {
            const { leaves, users, locations: companyLocs } = await fetchCompanyWideData()
            setCompanyLeaves(leaves) // This will be the unfiltered company leaves
            setCompanyUsers(users) // All company users
            setCompanyLocations(companyLocs) // All locations

            if (process.env.NODE_ENV === 'development') {
              console.log('[Dashboard] Using company-wide data:', {
                leavesCount: leaves.length,
                usersCount: users.length,
                locationsCount: companyLocs.length,
              })
            }
          } catch (error) {
            console.error('Failed to fetch company-wide users:', error)
            // Fallback to using existing data
            setCompanyLeaves(dataCompanyLeaves)
            setCompanyUsers(teamUsers)
            setCompanyLocations(locations)
          } finally {
            setIsLoadingCompanyData(false)
          }
        })()
      } else {
        // Reset to department view
        setCompanyLeaves([])
        setCompanyUsers([])
        setCompanyLocations([])
      }
    },
    [fetchCompanyWideData, dataCompanyLeaves, teamUsers, locations]
  )

  // Handle leave type changes with tracking
  const handleLeaveTypeChange = useCallback(
    (newLeaveTypeId: string) => {
      const currentYearLeaveTypes = user?.leaveDays.find((ld) => dayjs().isBetween(ld.yearStart, ld.yearEnd, 'day', '[]'))
      const leaveTypes = (currentYearLeaveTypes?.leaveTypes as IUserLeaveTypeResult[]) || []
      const fromLeaveType = leaveTypes.find((lt) => lt.leaveTypeId === selectedLeaveTypeId)
      const toLeaveType = leaveTypes.find((lt) => lt.leaveTypeId === newLeaveTypeId)

      if (fromLeaveType && toLeaveType) {
        dashboardTracker.trackLeaveTypeSwitched(
          fromLeaveType.leaveTypeName,
          toLeaveType.leaveTypeName,
          fromLeaveType.currentDays,
          toLeaveType.currentDays,
          leaveTypes.filter((lt) => lt.isActive).length,
          toLeaveType.shortestLeaveInterval ? ['ONE-HOUR', 'HALF-HOUR', 'FIFTEEN-MINUTES'].includes(toLeaveType.shortestLeaveInterval) : false,
          toLeaveType.shortestLeaveInterval || 'FULL-DAY'
        )

        // Track balance check for new leave type
        dashboardTracker.trackBalanceChecked({
          leaveTypeViewed: toLeaveType.leaveTypeName,
          balanceAmount: toLeaveType.currentDays,
          hasMinutesPrecision: toLeaveType.shortestLeaveInterval ? ['ONE-HOUR', 'HALF-HOUR', 'FIFTEEN-MINUTES'].includes(toLeaveType.shortestLeaveInterval) : false,
          isNegative: toLeaveType.currentDays < 0,
          isUnlimited: toLeaveType.hasUnlimitedDays,
        })
      }

      setSelectedLeaveTypeId(newLeaveTypeId)
    },
    [user?.leaveDays, selectedLeaveTypeId]
  )

  const handleCancelLeave = useCallback(
    async (leaveRequestId: string) => {
      try {
        if (!user) {
          throw new Error('Missing user')
        }

        const isToil = isToilLeave(leaveRequestId)
        const body = {
          eventType: isToil ? 'TOIL_REQUEST_CANCELLED' : 'LEAVE_REQUEST_CANCELLED',
          eventGroup: isToil ? 'USER_TOIL_REQUEST' : 'USER_LEAVE_REQUEST',
          userId: user.id,
        }
        isToil ? (body['toilRequestId'] = leaveRequestId) : (body['leaveRequestId'] = leaveRequestId)

        const response = await Api.post('/core/event', body)
        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: isToil ? 'components.toil.cancelInProgress' : 'user.cancelLeaveRequestInProgress' }),
          icon: <LoadingOutlined />,
          duration: 0,
        })

        // Track this operation so we know when to refetch
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dashboard] Tracking pending operation:', response.correlationId)
        }
        setActionNotifications([...actionNotifications, response.correlationId])
        setLocalActionNotifications([...localActionNotifications, response.correlationId])
      } catch (error) {
        notification.error({
          message: formatMessage({ id: 'error.somethingWentWrong' }),
          description: String(error),
        })
      }
    },
    [user, formatMessage]
  )

  // Wrapper for cancel leave to ensure void return
  const handleCancelLeaveWrapper = useCallback(
    (leaveId: string) => {
      void handleCancelLeave(leaveId)
    },
    [handleCancelLeave]
  )

  // Memoized refetch handler for error state
  const handleRetry = useCallback(() => {
    void refetch()
  }, [refetch])

  // Prepare data for memoization (safe to access before user check)
  const currentYearLeaveTypes = user?.leaveDays.find((ld) => dayjs().isBetween(ld.yearStart, ld.yearEnd, 'day', '[]'))
  const leaveTypes = (currentYearLeaveTypes?.leaveTypes || []) as IUserLeaveTypeResult[]
  const approvedUpcomingLeaves = (user?.upcomingLeaves || []) as unknown as IDashboardLeaveRequest[]
  const pendingLeaves = (user?.pendingLeaves || []) as unknown as IDashboardLeaveRequest[]
  const todayDate = dayjs().format('YYYY-MM-DD')
  // `today` returns everything overlapping today; the card only wants the ones that start today,
  // so a multi-day leave that is already running stays out.
  const leavesStartingToday = ((user?.today || []) as unknown as IDashboardLeaveRequest[]).filter((leave) => leave.startDate.substring(0, 10) === todayDate)

  // Memoize: Combine approved and pending leaves for the upcoming leaves section
  const upcomingLeaves = useMemo(
    () => [...leavesStartingToday, ...approvedUpcomingLeaves, ...pendingLeaves].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
    [leavesStartingToday, approvedUpcomingLeaves, pendingLeaves]
  )

  // Memoize: Calculate stats only when relevant data changes
  const quickStats = useMemo(
    () =>
      user
        ? calculateQuickStats(
            currentYearLeaveTypes?.yearStart || dayjs().startOf('year').format('YYYY-MM-DD'),
            currentYearLeaveTypes?.yearEnd || dayjs().endOf('year').format('YYYY-MM-DD'),
            leaveTypes,
            approvedUpcomingLeaves,
            pendingLeaves,
            user,
            company?.hourlyLeaveAccounting || false
          )
        : null,
    [leaveTypes, approvedUpcomingLeaves, pendingLeaves, user, company?.hourlyLeaveAccounting]
  )

  const getRolloverExpiry = (hourlyAccounting: boolean, quotas: IQuotas, rolloverFromPreviousYear: number, rolloverExpiryDate: string): IRolloverExpiry | null => {
    const totalRolloverDays = quotas?.rolloverFromPreviousYear?.raw.days || rolloverFromPreviousYear
    const totalRolloverHours = quotas?.rolloverFromPreviousYear?.raw.hours || rolloverFromPreviousYear // TODO: use hours
    const actualExpiredDays = quotas?.expired?.raw.days || 0
    const actualExpiredHours = quotas?.expired?.raw.hours || 0
    const usedDays = quotas?.used?.raw.days || 0
    const usedHours = quotas?.used?.raw.hours || 0

    // Check if rollover actually expires (not set to far future like year 3025)
    const expiryDate = dayjs(rolloverExpiryDate)
    const currentYear = dayjs().year()
    const expiryYear = expiryDate.year()

    // If expiry is more than 10 years in the future, treat as "never expires"
    if (expiryYear > currentYear + 10) {
      return null // Don't show warning for rollover that never expires
    }

    if (actualExpiredDays > 0 || actualExpiredHours > 0) {
      return {
        expired: hourlyAccounting ? roundTo2Decimals(actualExpiredHours) : roundTo2Decimals(actualExpiredDays),
        unit: hourlyAccounting ? 'hours' : 'days',
        totalRollover: hourlyAccounting ? totalRolloverHours : totalRolloverDays,
        leaveTypeName: selectedLeaveType?.leaveTypeName || '',
        expiryDate: dayjs.utc(expiryDate).format('MMMM DD, YYYY'),
      }
    }

    // Calculate scheduled days for this leave type that occur before expiry date
    // Only count APPROVED leaves (upcomingLeaves), not pending requests
    const scheduledBeforeExpiryLeaves = upcomingLeaves
      .filter((leave) => leave.status === 'APPROVED') // Only approved leaves
      .filter((leave) => leave.leaveTypeId === selectedLeaveType?.leaveTypeId) // Same leave type
      .filter((leave) => dayjs(leave.startDate).isBefore(expiryDate) || dayjs(leave.startDate).isSame(expiryDate, 'day')) // Before expiry

    const scheduledBeforeExpiryDays = scheduledBeforeExpiryLeaves.reduce((total: number, leave) => {
      // Use workingDays if available, otherwise estimate 1 day
      const days: number = leave.totalThisYear || 0
      return total + days
    }, 0)

    const scheduledBeforeExpiryHours = scheduledBeforeExpiryLeaves.reduce((total: number, leave) => {
      const hours: number = leave.daysList.reduce((total: number, day) => {
        return total + day.leaveHours
      }, 0)
      return total + hours
    }, 0)

    // Calculate what will expire: rollover - (used + scheduled before expiry)
    const willExpireDays = Math.max(totalRolloverDays - usedDays - scheduledBeforeExpiryDays, 0)
    const willExpireHours = Math.max(totalRolloverHours - usedHours - scheduledBeforeExpiryHours, 0)

    return {
      willExpire: hourlyAccounting ? roundTo2Decimals(willExpireHours) : roundTo2Decimals(willExpireDays),
      unit: hourlyAccounting ? 'hours' : 'days',
      expiryDate: dayjs.utc(expiryDate).format('MMMM DD, YYYY'),
      totalRollover: hourlyAccounting ? totalRolloverHours : totalRolloverDays,
      leaveTypeName: selectedLeaveType?.leaveTypeName || '',
    }
  }

  const rolloverExpiry = useMemo(() => {
    if (!selectedLeaveType?.quotas) return null
    return getRolloverExpiry(company?.hourlyLeaveAccounting || false, selectedLeaveType?.quotas, selectedLeaveType?.rolloverFromPreviousYear, selectedLeaveType?.rolloverExpiryDate)
  }, [company?.hourlyLeaveAccounting, selectedLeaveType])

  // Memoize: Generate alert banners only when dependencies change
  const alerts = useMemo(
    () => (user ? generateAlertBanners(leaveTypes, user, company?.hourlyLeaveAccounting || false, rolloverExpiry, formatMessage) : []),
    [leaveTypes, user, formatMessage, rolloverExpiry]
  )

  // Memoize: Date constants for team availability calculations
  const dateConstants = useMemo(
    () => ({
      today: dayjs(),
      tomorrow: dayjs().add(1, 'day'),
      nextWeekStart: dayjs().add(7, 'days'),
      nextWeekEnd: dayjs().add(14, 'days'),
    }),
    []
  ) // Empty deps - dates only change when component remounts

  // Memoize: Filter team leaves by date ranges
  const { teamLeavesToday, teamLeavesTomorrow, teamLeavesNextWeek } = useMemo(() => {
    const { today, tomorrow, nextWeekStart, nextWeekEnd } = dateConstants

    const leavesToday = teamLeaves.filter((leave) => {
      const startDate = dayjs(leave.startDate)
      const endDate = dayjs(leave.endDate)
      return (startDate.isSame(today, 'day') || startDate.isBefore(today, 'day')) && (endDate.isSame(today, 'day') || endDate.isAfter(today, 'day'))
    })

    const leavesTomorrow = teamLeaves.filter((leave) => {
      const startDate = dayjs(leave.startDate)
      const endDate = dayjs(leave.endDate)
      return (startDate.isSame(tomorrow, 'day') || startDate.isBefore(tomorrow, 'day')) && (endDate.isSame(tomorrow, 'day') || endDate.isAfter(tomorrow, 'day'))
    })

    const leavesNextWeek = teamLeaves.filter((leave) => {
      const startDate = dayjs(leave.startDate)
      const endDate = dayjs(leave.endDate)

      // Check if leave overlaps with next week (7-14 days from now)
      return (
        (startDate.isAfter(nextWeekStart.subtract(1, 'day')) && startDate.isBefore(nextWeekEnd.add(1, 'day'))) ||
        (endDate.isAfter(nextWeekStart.subtract(1, 'day')) && endDate.isBefore(nextWeekEnd.add(1, 'day'))) ||
        (startDate.isBefore(nextWeekStart) && endDate.isAfter(nextWeekEnd))
      )
    })

    return {
      teamLeavesToday: leavesToday,
      teamLeavesTomorrow: leavesTomorrow,
      teamLeavesNextWeek: leavesNextWeek,
    }
  }, [teamLeaves, dateConstants])

  // Memoize: Calculate team availability based on filtered leaves
  const teamAvailability = useMemo(
    () =>
      user
        ? calculateTeamAvailability((user.team?.approvers || []) as Array<{ id: string; name: string }>, teamLeavesToday, teamLeavesTomorrow, teamLeavesNextWeek)
        : { todayOut: [], tomorrowOut: [] },
    [user?.team?.approvers, teamLeavesToday, teamLeavesTomorrow, teamLeavesNextWeek]
  )

  // Memoize: Extract holidays from user location data
  const holidays = useMemo(() => {
    if (!user?.location?.holidays) return []

    const currentYear = new Date().getFullYear()

    // Get holidays from both current and next year
    const allHolidayObjects = user.location.holidays.filter((h) => h.year >= currentYear)
    const allHolidays = allHolidayObjects.map((h) => h.holidays).flat()

    return allHolidays as Array<{ name: string; date: string; isHalfDay?: boolean }>
  }, [user?.location?.holidays])

  // Memoize: Greeting and date formatting (changes only when locale changes)
  const { greeting, currentDate } = useMemo(
    () => ({
      greeting: getTimeBasedGreeting(formatMessage),
      currentDate: dayjs().format('dddd, MMMM DD, YYYY'),
    }),
    [formatMessage]
  )

  // Track error state when it occurs
  const [hasTrackedError, setHasTrackedError] = useState(false)

  useEffect(() => {
    if (error && !user && !hasTrackedError) {
      dashboardTracker.trackError({
        errorType: 'graphql_error',
        errorMessage: error.message || 'Unknown error',
        sectionAffected: 'dashboard',
        hasCachedData: false,
        retryAttempted: false,
        autoRecoveryPossible: true,
      })
      setHasTrackedError(true)
    }
  }, [error, user, hasTrackedError, authUser?.id])

  // Show error state
  if (error && !user) {
    return (
      <div className="user-dashboard">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Title level={3} type="danger">
            {formatMessage({ id: 'dashboard.error.title' })}
          </Title>
          <Text type="secondary">{formatMessage({ id: 'dashboard.error.description' })}</Text>
          <br />
          <Button
            type="primary"
            onClick={() => {
              // Track retry attempt as a separate error event
              dashboardTracker.trackError({
                errorType: 'graphql_error',
                errorMessage: error.message || 'Unknown error',
                sectionAffected: 'dashboard',
                hasCachedData: false,
                retryAttempted: true,
                autoRecoveryPossible: true,
              })
              setHasTrackedError(false) // Reset so we track again if error persists
              handleRetry()
            }}
            style={{ marginTop: 16 }}
          >
            {formatMessage({ id: 'dashboard.error.retry' })}
          </Button>
        </div>
      </div>
    )
  }

  // Show loading skeleton only when actually loading data
  if (isLoading && !user) {
    return (
      <div className="user-dashboard dashboard-loading">
        <div className="dashboard-header">
          <Skeleton active paragraph={{ rows: 1 }} />
        </div>
        <div className="primary-grid">
          <Skeleton active />
          <Skeleton active />
        </div>
        <Skeleton active paragraph={{ rows: 2 }} />
        <div className="secondary-grid">
          <Skeleton active />
          <Skeleton active />
        </div>
      </div>
    )
  }

  if (!user) return null

  return (
    <div className={`user-dashboard ${isFromCache ? 'updating-from-cache' : ''}`}>
      {/* Alert Banners */}
      {alerts.length > 0 && <AlertBanner alerts={alerts} />}

      {/* Header Section */}
      <div className="dashboard-header">
        <div className="greeting-section">
          <div className="greeting-text">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Title level={1} style={{ margin: 0 }}>
                {greeting}, {user.name}
              </Title>
              {isFromCache && cacheAge && (
                <Tag icon={<SyncOutlined spin />} color="processing" style={{ marginBottom: 8 }}>
                  {formatMessage({ id: 'dashboard.updatingData' })}
                </Tag>
              )}
            </div>
            <div className="date-info">{currentDate}</div>
          </div>
          <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleRequestLeave} className="request-leave-btn">
            <IntlMessages id="app.requestLeave" />
          </Button>
        </div>
      </div>

      {/* Primary Grid - Balance and Upcoming Leaves */}
      <div className="primary-grid">
        <BalanceCard
          currentYearLeaveTypes={currentYearLeaveTypes?.leaveTypes || []}
          currentYearStart={currentYearLeaveTypes?.yearStart || ''}
          currentYearEnd={currentYearLeaveTypes?.yearEnd || ''}
          selectedLeaveType={selectedLeaveType}
          onLeaveTypeChange={handleLeaveTypeChange}
          user={user}
          hourlyAccounting={company?.hourlyLeaveAccounting || false}
          isLoading={isLoading}
          rolloverExpiry={rolloverExpiry}
        />

        <UpcomingLeavesCard
          upcomingLeaves={upcomingLeaves}
          onRequestLeave={handleRequestLeave}
          onViewAll={handleViewAllLeaves}
          onViewProfile={handleViewProfile}
          onCancelLeave={handleCancelLeaveWrapper}
        />
      </div>

      {/* Team Availability Section - Hide when visibility is PERSONAL */}
      {visibilityAddon?.[0]?.visibilityLevel?.[0] !== 'PERSONAL' && (
        <TeamAvailability
          teamName={user.team?.name}
          todayOut={teamAvailability.todayOut}
          allTeamLeaves={teamLeaves}
          allTeamUsers={teamUsers}
          allCompanyLeaves={companyLeaves.length > 0 ? companyLeaves : teamLeaves}
          allCompanyUsers={companyUsers.length > 0 ? companyUsers : teamUsers}
          locations={companyLocations.length > 0 ? companyLocations : locations}
          currentUserWorkHours={user.workHours}
          viewerHourFormat={authUser?.hourFormat || HourFormatEnum.twentyFour}
          isLoading={isLoading || isLoadingCompanyData}
          visibilityLevel={visibilityAddon?.[0]?.visibilityLevel?.[0] || null}
          onViewScopeChange={handleViewScopeChange}
        />
      )}

      {/* Secondary Grid - Stats, Holidays */}
      <div className="secondary-grid">
        {quickStats && <QuickStats stats={quickStats} isLoading={isLoading} />}

        <UpcomingHolidays holidays={holidays} />
      </div>
    </div>
  )
}

export default DashboardPageForUsers
